import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Typography, Paper, Grid, Card, Box,
  Button, CircularProgress, Stack,
  styled, Container, IconButton, Dialog, Divider, Chip, Tooltip
} from '@mui/material';
import {
  PhotoLibrary, AutoAwesome, Close,
  ArrowBackIosNew, LocationOn, CalendarMonth,
  PeopleAlt, Message, CheckCircle, Cancel,
  Person, Groups, HowToReg, Schedule, ImageSearch
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/* ─── Design Tokens ─── */
const T = {
  amber:    '#C2823C',
  amberLight: '#E8A85A',
  ink:      '#1a1510',
  inkMid:   '#4a3f30',
  inkLight: '#8a7055',
  cream:    '#FAF8F4',
  ivory:    '#FFFEFB',
  border:   'rgba(194,130,60,0.15)',
  sage:     '#6B8C6E',
  rose:     '#C4706A',
  slate:    '#5A6880',
};

/* ─── Source badge config ─── */
const SOURCE = {
  organizer: { label: 'Organizer',  color: T.amber,  bg: `${T.amber}18`,  icon: <Person sx={{ fontSize: 11 }} /> },
  guest:     { label: 'Guest',      color: T.sage,   bg: `${T.sage}18`,   icon: <Groups sx={{ fontSize: 11 }} /> },
  approved:  { label: 'Approved',   color: T.sage,   bg: `${T.sage}18`,   icon: <HowToReg sx={{ fontSize: 11 }} /> },
  pending:   { label: 'Pending',    color: T.amber,  bg: `${T.amber}18`,  icon: <Schedule sx={{ fontSize: 11 }} /> },
  rejected:  { label: 'Rejected',   color: T.rose,   bg: `${T.rose}18`,   icon: <Cancel sx={{ fontSize: 11 }} /> },
};

/* ─── Styled Components ─── */
const MasonryGrid = styled(Box)(({ theme }) => ({
  columnCount: 4,
  columnGap: '16px',
  [theme.breakpoints.down('md')]: { columnCount: 3 },
  [theme.breakpoints.down('sm')]: { columnCount: 2 },
}));

const PhotoWrapper = styled(Box)(() => ({
  breakInside: 'avoid',
  marginBottom: '16px',
  position: 'relative',
}));

const PhotoCard = styled(Card)(() => ({
  borderRadius: '16px',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${T.border}`,
  transition: 'all 0.35s ease',
  cursor: 'zoom-in',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 15px 30px rgba(194,130,60,0.2)',
    '& .overlay': { opacity: 1 },
  },
}));

const PhotoOverlay = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 55%)',
  display: 'flex',
  alignItems: 'flex-end',
  padding: '10px',
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const SourceBadge = ({ source, reviewStatus }) => {
  const cfg = reviewStatus ? SOURCE[reviewStatus] : SOURCE[source] || SOURCE.organizer;
  return (
    <Box sx={{
      position: 'absolute', top: 8, left: 8, zIndex: 2,
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      px: '7px', py: '3px', borderRadius: '50px',
      background: cfg.bg, border: `1px solid ${cfg.color}30`,
      backdropFilter: 'blur(6px)',
    }}>
      <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>{cfg.icon}</Box>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: cfg.color, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
        {cfg.label}
      </Typography>
    </Box>
  );
};

const ManagementPanel = styled(Paper)(() => ({
  padding: '24px',
  borderRadius: '24px',
  background: T.ivory,
  border: `1px solid ${T.border}`,
  height: '100%',
  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
}));

/* ─── Photo Request Review Panel ─── */
const PhotoRequestPanel = ({ photoRequests, onRefresh }) => {
  if (!photoRequests || photoRequests.length === 0) return null;

  const totalUploaded = photoRequests.reduce((s, r) => s + r.upload_count, 0);
  const totalApproved = photoRequests.reduce((s, r) => s + r.approved_count, 0);
  const totalPending  = photoRequests.reduce((s, r) => s + r.pending_count, 0);

  return (
    <ManagementPanel sx={{ mb: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <ImageSearch sx={{ color: T.amber }} />
        <Typography variant="h5" sx={{ fontFamily: "'DM Serif Display', serif" }}>Guest Photo Requests</Typography>
      </Stack>

      {/* Summary chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Chip label={`${totalUploaded} uploaded`}  size="small" sx={{ bgcolor: `${T.slate}15`,  color: T.slate,  fontWeight: 700, fontSize: '0.7rem' }} />
        <Chip label={`${totalApproved} approved`}  size="small" sx={{ bgcolor: `${T.sage}15`,   color: T.sage,   fontWeight: 700, fontSize: '0.7rem' }} />
        <Chip label={`${totalPending} pending`}    size="small" sx={{ bgcolor: `${T.amber}15`,  color: T.amber,  fontWeight: 700, fontSize: '0.7rem' }} />
      </Stack>

      <Stack spacing={2} sx={{ maxHeight: 420, overflowY: 'auto', pr: 1 }}>
        {photoRequests.map((req, i) => (
          <Box key={i} sx={{ p: 2, borderRadius: '14px', border: `1px solid ${T.border}`, bgcolor: T.cream }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink }}>{req.guest_email}</Typography>
              <Chip
                label={req.status}
                size="small"
                sx={{
                  fontSize: '0.62rem', fontWeight: 700,
                  bgcolor: req.status === 'reviewed' ? `${T.sage}15` : req.status === 'uploaded' ? `${T.amber}15` : `${T.slate}15`,
                  color:   req.status === 'reviewed' ? T.sage        : req.status === 'uploaded' ? T.amber        : T.slate,
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography variant="caption" sx={{ color: T.inkLight }}>
                Sent: {new Date(req.created_at).toLocaleDateString()}
              </Typography>
              <Typography variant="caption" sx={{ color: T.inkLight }}>
                Expires: {new Date(req.expires_at).toLocaleDateString()}
              </Typography>
            </Stack>

            {/* Uploaded photos thumbnails */}
            {req.uploaded_photos?.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: T.inkLight, fontWeight: 600, display: 'block', mb: 1 }}>
                  {req.uploaded_photos.length} photo{req.uploaded_photos.length !== 1 ? 's' : ''} uploaded
                </Typography>
                <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                  {req.uploaded_photos.map((p, j) => {
                    const url = p.photo_url.startsWith('http')
                      ? p.photo_url
                      : `${process.env.REACT_APP_UPLOAD_PHOTOS_API}/${p.photo_url}`;
                    return (
                      <Tooltip key={j} title={`${p.status} · ${p.original_name}`} arrow>
                        <Box sx={{
                          width: 48, height: 48, borderRadius: '8px', overflow: 'hidden',
                          border: `2px solid ${
                            p.status === 'approved' ? T.sage :
                            p.status === 'rejected' ? T.rose : T.amber
                          }`,
                          flexShrink: 0, position: 'relative',
                        }}>
                          <img src={url} alt={p.original_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <Box sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: p.status === 'approved' ? `${T.sage}40` :
                                        p.status === 'rejected' ? `${T.rose}40` : 'transparent',
                          }}>
                            {p.status === 'approved' && <CheckCircle sx={{ fontSize: 16, color: '#fff' }} />}
                            {p.status === 'rejected' && <Cancel      sx={{ fontSize: 16, color: '#fff' }} />}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </ManagementPanel>
  );
};

/* ─── Main Component ─── */
const EventDetails = ({ eventId, userId, onClose }) => {
  const navigate = useNavigate();
  const [eventData,     setEventData]     = useState({});
  const [fetchedPhotos, setFetchedPhotos] = useState([]);
  const [photoRequests, setPhotoRequests] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // { url, source, reviewStatus }
  const [loading,       setLoading]       = useState(true);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_EVENT_DATA_API}/${eventId}`);
      setEventData(res.data);
      const event = res.data.event || {};

      if (event.eventName) {
        // ── Organizer photos ──
        const photoRes  = await fetch(
          `${process.env.REACT_APP_LIST_PHOTOS_API}/${userId}/${encodeURIComponent(event.eventName)}`
        );
        const photoData = await photoRes.json();
        const orgPhotos = (photoData.images || []).map(img => ({
          url:    img.startsWith('http')
            ? img
            : `${process.env.REACT_APP_UPLOAD_PHOTOS_API}/${userId}/${encodeURIComponent(event.eventName)}/${img}`,
          source: 'organizer',
        }));

        // ── Guest photo requests ──
        let guestPhotos = [];
        try {
          const reqRes  = await axios.get(
            `${process.env.REACT_APP_PHOTO_REQUESTS_API}/${userId}?event_id=${eventId}`
          );
          const requests = reqRes.data?.requests || [];
          setPhotoRequests(requests);

          // Flatten all uploaded guest photos into the gallery
          requests.forEach(req => {
            (req.uploaded_photos || []).forEach(p => {
              const url = p.photo_url.startsWith('http')
                ? p.photo_url
                : `${process.env.REACT_APP_UPLOAD_PHOTOS_API}/${p.photo_url}`;
              guestPhotos.push({
                url,
                source:        'guest',
                reviewStatus:  p.status,       // 'pending' | 'approved' | 'rejected'
                guestEmail:    req.guest_email,
                originalName:  p.original_name,
              });
            });
          });
        } catch (e) {
          console.warn('Photo requests not available:', e);
        }

        setFetchedPhotos([...orgPhotos, ...guestPhotos]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [eventId, userId]);

  const updateUserStatus = async (contact, status) => {
    try {
      await axios.post(process.env.REACT_APP_UPDATE_CONTACT_STATUS_API, { eventId, contact, status });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();   // closes the dialog inside Dashboard — preferred
    } else {
      navigate('/DashBoard', { replace: true });
    }
  };

  if (loading) return (
    <Box textAlign="center" py={10}>
      <CircularProgress sx={{ color: T.amber }} />
    </Box>
  );

  const { event = {}, accessRequests = [], scheduledMessages = [] } = eventData;

  const orgCount   = fetchedPhotos.filter(p => p.source === 'organizer').length;
  const guestCount = fetchedPhotos.filter(p => p.source === 'guest').length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      {/* ─── 1. Hero ─── */}
      <Paper sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', borderRadius: '32px', bgcolor: T.ivory, border: `1px solid ${T.border}`, mb: 4 }}>
        <Typography variant="overline" sx={{ color: T.amber, fontWeight: 700, letterSpacing: 2 }}>Event Dossier</Typography>
        <Typography variant="h2" sx={{ fontFamily: "'DM Serif Display', serif", color: T.ink, my: 2 }}>
          {event.eventName}
        </Typography>
        <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonth sx={{ color: T.amber }} />
            <Typography>{new Date(event.eventDate).toLocaleDateString()}</Typography>
          </Stack>
          {event.location && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOn sx={{ color: T.amber }} />
              <Typography>{event.location}</Typography>
            </Stack>
          )}
        </Stack>
        {event.description && (
          <>
            <Divider sx={{ width: '40px', mx: 'auto', mb: 3 }} />
            <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'gray', maxWidth: 600, mx: 'auto' }}>
              {event.description}
            </Typography>
          </>
        )}
      </Paper>

      {/* ─── 2. Gallery ─── */}
      <Box sx={{ p: 4, bgcolor: T.cream, borderRadius: '32px', border: `1px solid ${T.border}`, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
          <PhotoLibrary sx={{ color: T.amber }} />
          <Typography variant="h4" sx={{ fontFamily: "'DM Serif Display', serif" }}>Captured Moments</Typography>
        </Stack>

        {/* Photo count breakdown */}
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 4 }}>
          <Chip
            icon={<Person sx={{ fontSize: '0.8rem !important', color: `${T.amber} !important` }} />}
            label={`${orgCount} by organizer`}
            size="small"
            sx={{ bgcolor: `${T.amber}12`, color: T.amber, fontWeight: 600, fontSize: '0.7rem' }}
          />
          {guestCount > 0 && (
            <Chip
              icon={<Groups sx={{ fontSize: '0.8rem !important', color: `${T.sage} !important` }} />}
              label={`${guestCount} from guests`}
              size="small"
              sx={{ bgcolor: `${T.sage}12`, color: T.sage, fontWeight: 600, fontSize: '0.7rem' }}
            />
          )}
        </Stack>

        {fetchedPhotos.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography sx={{ color: T.inkLight }}>No photos yet.</Typography>
          </Box>
        ) : (
          <MasonryGrid>
            {fetchedPhotos.map((photo, i) => (
              <PhotoWrapper key={i}>
                <PhotoCard elevation={0} onClick={() => setSelectedPhoto(photo)}>
                  <SourceBadge source={photo.source} reviewStatus={photo.source === 'guest' ? photo.reviewStatus : null} />
                  <img src={photo.url} alt="" style={{ width: '100%', display: 'block' }} />
                  <PhotoOverlay className="overlay">
                    <Stack spacing={0.3}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#fff' }}>
                        <AutoAwesome sx={{ fontSize: 13 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                          VIEW SHOT
                        </Typography>
                      </Stack>
                      {photo.source === 'guest' && photo.guestEmail && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>
                          {photo.guestEmail}
                        </Typography>
                      )}
                    </Stack>
                  </PhotoOverlay>
                </PhotoCard>
              </PhotoWrapper>
            ))}
          </MasonryGrid>
        )}
      </Box>

      {/* ─── 3. Management Section ─── */}
      <Grid container spacing={4} sx={{ mb: 4 }}>

        {/* Assigned Guests */}
        <Grid item xs={12} md={6}>
          <ManagementPanel>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <PeopleAlt sx={{ color: T.amber }} />
              <Typography variant="h5" sx={{ fontFamily: "'DM Serif Display', serif" }}>Assigned Guests</Typography>
            </Stack>
            <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              {accessRequests.flatMap(r => r.assignedUsers).map((user, i) => (
                <Box key={i} sx={{ p: 2, borderRadius: '16px', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.contact}</Typography>
                    <Chip
                      label={user.status}
                      size="small"
                      sx={{
                        mt: 0.5, fontSize: '0.65rem', fontWeight: 700,
                        bgcolor: user.status === 'approved' ? `${T.sage}15` : `${T.amber}15`,
                        color:   user.status === 'approved' ? T.sage        : T.amber,
                      }}
                    />
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => updateUserStatus(user.contact, 'approved')} sx={{ color: T.sage }}>
                      <CheckCircle />
                    </IconButton>
                    <IconButton size="small" onClick={() => updateUserStatus(user.contact, 'denied')} sx={{ color: T.rose }}>
                      <Cancel />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
              {accessRequests.length === 0 && (
                <Typography variant="body2" sx={{ color: 'gray', py: 2 }}>No guests assigned yet.</Typography>
              )}
            </Stack>
          </ManagementPanel>
        </Grid>

        {/* Scheduled Messages */}
        <Grid item xs={12} md={6}>
          <ManagementPanel>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <Message sx={{ color: T.amber }} />
              <Typography variant="h5" sx={{ fontFamily: "'DM Serif Display', serif" }}>Message Logs</Typography>
            </Stack>
            <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              {scheduledMessages.map((msg, i) => (
                <Box key={i} sx={{ p: 2, borderRadius: '16px', bgcolor: T.cream, border: `1px solid ${T.border}` }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: T.inkMid }}>
                      {msg.status.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'gray' }}>
                      {new Date(msg.send_time).toLocaleDateString()}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: T.inkMid }}>
                    <strong>To:</strong> {msg.recipients.join(', ')}
                  </Typography>
                </Box>
              ))}
              {scheduledMessages.length === 0 && (
                <Typography variant="body2" sx={{ color: 'gray', py: 2 }}>No message history.</Typography>
              )}
            </Stack>
          </ManagementPanel>
        </Grid>

        {/* Guest Photo Requests panel — full width */}
        {photoRequests.length > 0 && (
          <Grid item xs={12}>
            <PhotoRequestPanel photoRequests={photoRequests} onRefresh={fetchData} />
          </Grid>
        )}
      </Grid>

      {/* ─── 4. Lightbox ─── */}
      <Dialog
        fullScreen
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.93)' } }}
      >
        {/* Close button — always visible */}
        <IconButton
          onClick={() => setSelectedPhoto(null)}
          sx={{
            position: 'fixed', right: 20, top: 20, zIndex: 10,
            color: '#fff',
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': { background: 'rgba(255,255,255,0.25)' },
          }}
        >
          <Close />
        </IconButton>

        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <img
            src={selectedPhoto?.url}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 120px)', borderRadius: '12px', objectFit: 'contain' }}
          />

          {/* Photo metadata bar at the bottom */}
          {selectedPhoto && (
            <Box sx={{
              mt: 2, px: 3, py: 1.5,
              borderRadius: '50px',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', gap: 2,
            }}>
              {/* Source badge */}
              {selectedPhoto.source === 'organizer' ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Person sx={{ fontSize: 14, color: T.amberLight }} />
                  <Typography sx={{ fontSize: '0.75rem', color: T.amberLight, fontWeight: 600 }}>
                    Organizer photo
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Groups sx={{ fontSize: 14, color: '#a8d5b5' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#a8d5b5', fontWeight: 600 }}>
                    Guest photo · {selectedPhoto.guestEmail}
                  </Typography>
                </Stack>
              )}

              {/* Review status for guest photos */}
              {selectedPhoto.source === 'guest' && (
                <>
                  <Box sx={{ width: '1px', height: 16, bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Typography sx={{
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: selectedPhoto.reviewStatus === 'approved' ? '#a8d5b5'
                         : selectedPhoto.reviewStatus === 'rejected' ? '#f0a0a0'
                         : T.amberLight,
                  }}>
                    {selectedPhoto.reviewStatus}
                  </Typography>
                </>
              )}

              {/* Original filename */}
              {selectedPhoto.originalName && (
                <>
                  <Box sx={{ width: '1px', height: 16, bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                    {selectedPhoto.originalName}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Dialog>


    </Container>
  );
};

export default EventDetails;